import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { Alert, Button, TextLink } from "@boozallen-uip/uswds-react-core";
import { EvaluationContext } from "../stores/EvaluationContext";
import { CatalogContext } from "../stores/CatalogContext";
import { Header, Pager, PagerLink, LinkToGuidance } from "../components";
import { anchorSvg } from "../components/HeaderWithAnchor";
import ExternalLinkIcon from "../../ExternalLinkIcon.component";
import "../components/Criteria.css";
import useReport from "../../../hooks/useReport.hook";
import honourFragmentIdLinks from "../../../utils/honourFragmentIdLinks";
import AcrDownloadMenu from "../components/AcrDownloadMenu";
import {
  criteriaGuidanceHref,
  DEFAULT_OPENACR_TERMS,
  componentLabelForReport,
  getCatalogChapter,
  isWcagStandard,
  isStandardChapterIncludedInReport,
  levelCountWithLevel,
  mapChapterIdForExport,
  pickDisplayStandards,
  reportChapterHeadingLabel,
  reportCriterionRowId,
  reportHeadingForStandard,
} from "../utils/acrExport/openAcrReportShared";
import reportFilename from "../utils/reportFilename";
import {
  ACR_EDITOR_ABOUT_URL,
  ACR_EDITOR_CHAPTER_URL,
  ACR_EDITOR_GLOSSARY_URL,
  UNKNOWN_PRODUCT_WARNING,
} from "../../../utlities/core.constants";
import useEditorAccess from "../../../hooks/useEditorAccess.hook";
import { useLoginModal } from "../../modals/LoginModalContext";
import {
  formatIsoDateToUs,
  validateAboutReportFields,
} from "../utils/reportValidation";
import "./Report.page.css";

function optionalBlock(value, node) {
  if (value == null || String(value).trim() === "") return null;
  return node;
}

function contactLine(label, value) {
  const display = value != null && String(value).trim() !== "" ? String(value) : "—";
  return (
    <>
      {label}
      :
      {" "}
      {display}
    </>
  );
}

/** Same anchored header row + divider as About/Chapter (Criteria.css), without collapse control. */
function ReportPreviewSection({
  id, level, title, children, asSection,
}) {
  const HeadingTag = level === 2 ? "h2" : "h3";
  const Root = asSection ? "section" : "div";
  return (
    <Root
      className="criteria-details criteria-details--no-disclosure"
      aria-labelledby={asSection ? id : undefined}
    >
      <div className="criteria-section-header">
        <Link
          to={`#${id}`}
          className="criteria-anchor-link"
          aria-label={`Anchor link for ${typeof title === "string" ? title : "section"}`}
        >
          {anchorSvg}
        </Link>
        <span className="criteria-summary-inner">
          <HeadingTag id={id} className="criteria-title">
            {title}
          </HeadingTag>
        </span>
      </div>
      {children}
    </Root>
  );
}

ReportPreviewSection.propTypes = {
  id: PropTypes.string.isRequired,
  level: PropTypes.oneOf([2, 3]),
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  asSection: PropTypes.bool,
};

ReportPreviewSection.defaultProps = {
  level: 2,
  children: null,
  asSection: false,
};

/** Collapsible chapter table; controlled open state so tables start expanded (React 18 <details defaultOpen> is unreliable). */
function ReportChapterTableDetails({ id, heading, children }) {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <details
      className="criteria-details acr-report-preview__chapter-table-details mb-6"
      open={isOpen}
      onToggle={(e) => setIsOpen(e.currentTarget.open)}
    >
      <summary className="acr-report-preview__chapter-table-summary">
        <span className="criteria-summary-inner">
          {/*
            Use role="heading" instead of <h3> inside <summary> for a clearer outline; id supports
            in-page anchors and programmatic focus (tabIndex={-1}).
          */}
          <span
            id={id}
            role="heading"
            aria-level={3}
            tabIndex={-1}
            className="acr-report-preview__subheading acr-report-preview__subheading--chapter-table"
          >
            {heading}
          </span>
        </span>
      </summary>
      <div className="criteria-content mt-2">
        {children}
      </div>
    </details>
  );
}

ReportChapterTableDetails.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  children: PropTypes.node,
};

ReportChapterTableDetails.defaultProps = {
  children: null,
};

const SERVER_SUBMIT_FAILURE_MESSAGE = "Your report passed validation, but it has not been submitted successfully. Please try submitting again. If an error persists, reach out to acr.repository@gsa.gov.";
const MISSING_REPORT_ID_MESSAGE = "No report ID was found for this session. Open the About page and ensure a product is selected. If you just picked a product or changed your setup, wait a moment for autosave to finish, then try submitting again.";

/**
 * Report page: read-only review of the completed ACR.
 * Preview layout matches GSA openacr-editor Report (table-based criteria, standards grouping).
 */
export default function Report() {
  const location = useLocation();
  const { evaluation, clearPersistedDraft } = useContext(EvaluationContext);
  const { getCatalog } = useContext(CatalogContext);
  const { completeReport } = useReport();
  // Anonymous / unauthorized users are in Public Mode: download stays available, but submit is
  // disabled since there is no session and no server-side report to finalize.
  const isPublicMode = !useEditorAccess();
  const { openLoginModal } = useLoginModal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const [submitErrorType, setSubmitErrorType] = useState(null);

  useEffect(() => {
    honourFragmentIdLinks(location);
  }, [location]);

  const catalog = useMemo(
    () => getCatalog(evaluation?.catalog),
    [getCatalog, evaluation?.catalog]
  );
  const validationResult = useMemo(
    () => validateAboutReportFields(evaluation),
    [evaluation]
  );
  const chapters = catalog?.chapters ?? [];
  const lastChapter = chapters.length > 0 ? chapters[chapters.length - 1] : null;
  const conformanceLevels = catalog?.conformanceLevels ?? [];
  const hasCatalogToExport = Boolean(evaluation?.catalog && chapters.length > 0);
  const reportActionsEnabled = useMemo(
    () => hasCatalogToExport && validationResult.isValid,
    [hasCatalogToExport, validationResult.isValid]
  );

  const displayStandards = useMemo(() => {
    const list = catalog?.standards ?? [];
    return pickDisplayStandards(list).map((s) => ({
      ...s,
      report_heading: reportHeadingForStandard(s),
    }));
  }, [catalog]);

  const termsList = useMemo(
    () => (Array.isArray(catalog?.terms) && catalog.terms.length > 0
      ? catalog.terms
      : DEFAULT_OPENACR_TERMS),
    [catalog]
  );

  const getLevelLabel = useCallback((levelId) => {
    if (levelId == null || levelId === "") return "—";
    const level = conformanceLevels.find(
      (l) => String(l.name) === String(levelId)
    );
    return level?.label ?? levelId;
  }, [conformanceLevels]);

  const getChapterDisplayLabel = useCallback((standard, sourceChapterId) => {
    const mappedId = mapChapterIdForExport(sourceChapterId);
    if (isWcagStandard(standard)) {
      return reportChapterHeadingLabel(mappedId);
    }
    const standardBlob = `${standard?.label ?? ""} ${standard?.name ?? ""} ${standard?.id ?? ""}`
      .toLowerCase();
    if (standardBlob.includes("508")) {
      return reportChapterHeadingLabel(mappedId);
    }
    const catalogChapter = getCatalogChapter(catalog, sourceChapterId);
    return catalogChapter?.label ?? catalogChapter?.short_label ?? mappedId;
  }, [catalog]);

  if (!evaluation?.catalog) {
    return (
      <div className="space-y-4">
        <Header>Report</Header>
        <p className="text-gray-600">
          Select a catalog on the About page to load chapters, then fill out the criteria. Your completed ACR will appear here for review.
        </p>
        <Pager label="Previous/Next">
          <PagerLink to={ACR_EDITOR_ABOUT_URL} direction="previous">
            About
          </PagerLink>
          <PagerLink to={ACR_EDITOR_GLOSSARY_URL} direction="next">
            Glossary
          </PagerLink>
        </Pager>
      </div>
    );
  }

  const product = evaluation?.product ?? {};
  const vendor = evaluation?.vendor ?? {};
  const author = evaluation?.author ?? {};
  const catalogTitle = catalog?.title || catalog?.name || catalog?.label || "";
  const titleLead = (evaluation?.title ?? "").trim() || product?.name || "Report";
  // Unknown products (not in the author's org) are download-only: keep download enabled but
  // disable submit, since there is no autosaved report in the repository to finalize.
  const isUnknownProduct = Boolean(evaluation?.product_is_unknown);
  const isDownloadEnabled = reportActionsEnabled;
  const isSubmitEnabled = reportActionsEnabled && !isUnknownProduct && !isPublicMode;

  const handleSubmit = useCallback(async () => {
    if (!isSubmitEnabled || isSubmitting || submitSucceeded) return;
    if (!evaluation?.report_id) {
      setSubmitErrorType("missing-report-id");
      return;
    }

    setSubmitSucceeded(false);
    setSubmitErrorType(null);
    setIsSubmitting(true);
    try {
      const response = await completeReport(evaluation.report_id);
      const ok = response && response.status >= 200 && response.status < 300;
      if (ok) {
        setSubmitSucceeded(true);
        // The report is now finalized server-side, so the local crash-recovery copy is no longer
        // needed (GSAACR-356). Cleared here on confirmed success — never on button press.
        clearPersistedDraft();
        return;
      }
      setSubmitErrorType("server");
    } catch {
      setSubmitErrorType("server");
    } finally {
      setIsSubmitting(false);
    }
  }, [evaluation?.report_id, isSubmitEnabled, isSubmitting, submitSucceeded, completeReport, clearPersistedDraft]);

  return (
    <div className="space-y-8">
      <Header>Report</Header>
      <ReportStatusBanner
        validationResult={validationResult}
        submitSucceeded={submitSucceeded}
        productId={evaluation?.product?.id}
      />
      {submitErrorType && (
        <SubmitFailureBanner
          errorType={submitErrorType}
          serverMessage={SERVER_SUBMIT_FAILURE_MESSAGE}
          missingReportMessage={MISSING_REPORT_ID_MESSAGE}
        />
      )}
      {!isPublicMode && isUnknownProduct && (
        <div role="region" aria-label="Download-only report notice">
          <Alert type="warning" heading="Download-only report">
            {UNKNOWN_PRODUCT_WARNING}
          </Alert>
        </div>
      )}
      <p className="text-gray-600">
        Review your Accessibility Conformance Report below.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <AcrDownloadMenu
          evaluation={evaluation}
          catalog={catalog}
          enabled={isDownloadEnabled}
          menuButtonId="report-download-menu-button"
          menuId="report-download-menu"
        />
        <Button
          id="report-submit-button"
          type="button"
          onClick={handleSubmit}
          disabled={!isSubmitEnabled || isSubmitting || submitSucceeded}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </Button>
        {isPublicMode && (
          <span className="text-gray-600">
            <button
              type="button"
              onClick={openLoginModal}
              className="text-accent-cool underline bg-transparent border-0 p-0 cursor-pointer"
            >
              Log in
            </button>
            {" "}to submit
          </span>
        )}
      </div>

      <article
        className="acr-report-preview border-t border-gray-200 pt-8"
        aria-labelledby="acr-report-preview-heading"
      >
        <h1 id="acr-report-preview-heading" className="acr-report-preview__h1">
          <span className="evaluation-title">{titleLead}</span>
          {" "}
          Accessibility Conformance Report
        </h1>
        <p className="acr-report-preview__lede text-gray-800">
          Based on
          {" "}
          {catalogTitle}
        </p>

        <ReportPreviewSection id="report-name-of-product-version" title="Name of Product/Version">
          <p className="mt-0 mb-0 text-gray-900">
            {product?.name || "—"}
            {product?.version ? ` ${product.version}` : ""}
          </p>
        </ReportPreviewSection>

        <ReportPreviewSection id="report-dates-version" title="Report Dates and Version">
          <ul className="acr-report-preview__contact-list mb-0">
            <li>
              Report Date:
              {" "}
              {formatIsoDateToUs(evaluation?.report_date) || "—"}
            </li>
            <li>
              Last Modified Date:
              {" "}
              {evaluation?.last_modified_date || "—"}
            </li>
            <li>
              Version:
              {" "}
              {reportFilename(evaluation, true)}
            </li>
          </ul>
        </ReportPreviewSection>

        {optionalBlock(product?.description, (
          <ReportPreviewSection id="report-product-description" title="Product Description">
            <p className="acr-report-preview__multiline text-gray-900 mb-0">{product.description}</p>
          </ReportPreviewSection>
        ))}

        <ReportPreviewSection id="report-contact-information" title="Contact Information">
          <h3 id="report-author-information" className="acr-report-preview__subheading">
            Author Information
          </h3>
          <ul className="acr-report-preview__contact-list mb-0">
            <li>{contactLine("Name", author?.name)}</li>
            <li>{contactLine("Company", author?.company_name ?? author?.organization)}</li>
            <li>{contactLine("Address", author?.address)}</li>
            <li>
              Email:
              {" "}
              {author?.email ? (
                <a className="usa-link" href={`mailto:${author.email}`}>{author.email}</a>
              ) : "—"}
            </li>
            <li>{contactLine("Phone", author?.phone)}</li>
            <li>
              Website:
              {" "}
              {author?.website ? (
                <TextLink href={author.website} target="_blank" rel="noopener noreferrer">
                  {author.website}
                  <ExternalLinkIcon />
                  <span className="usa-sr-only"> (opens in new window)</span>
                </TextLink>
              ) : "—"}
            </li>
          </ul>
          <h3 id="report-vendor-information" className="acr-report-preview__subheading">
            Vendor Information
          </h3>
          <ul className="acr-report-preview__contact-list mb-0">
            <li>{contactLine("Name", vendor?.name)}</li>
            <li>{contactLine("Company", vendor?.company_name)}</li>
            <li>{contactLine("Address", vendor?.address)}</li>
            <li>
              Email:
              {" "}
              {vendor?.email ? (
                <a className="usa-link" href={`mailto:${vendor.email}`}>{vendor.email}</a>
              ) : "—"}
            </li>
            <li>{contactLine("Phone", vendor?.phone)}</li>
            <li>
              Website:
              {" "}
              {vendor?.website ? (
                <TextLink href={vendor.website} target="_blank" rel="noopener noreferrer">
                  {vendor.website}
                  <ExternalLinkIcon />
                  <span className="usa-sr-only"> (opens in new window)</span>
                </TextLink>
              ) : "—"}
            </li>
          </ul>
        </ReportPreviewSection>

        {optionalBlock(evaluation?.notes, (
          <ReportPreviewSection id="report-notes" title="Notes">
            <p className="acr-report-preview__multiline text-gray-900 mb-0">{evaluation.notes}</p>
          </ReportPreviewSection>
        ))}

        {optionalBlock(evaluation?.evaluation_methods_used, (
          <ReportPreviewSection id="report-evaluation-methods" title="Evaluation Methods Used">
            <p className="acr-report-preview__multiline text-gray-900 mb-0">
              {evaluation.evaluation_methods_used}
            </p>
          </ReportPreviewSection>
        ))}

        <ReportPreviewSection id="report-applicable-standards" title="Applicable Standards/Guidelines">
          <p className="mb-2 text-gray-800">
            This report covers the degree of conformance for the following accessibility standard/guidelines:
          </p>
          <div className="overflow-x-auto mb-0">
          <table className="usa-table usa-table--borderless acr-report-preview__standards-table w-full max-w-full">
            <caption className="usa-sr-only">
              Applicable standards and guidelines and which chapters are included in this report
            </caption>
            <thead>
              <tr>
                <th scope="col">Standard/Guideline</th>
                <th scope="col">Included In Report</th>
              </tr>
            </thead>
            <tbody>
              {displayStandards.map((std) => (
                <tr key={std.id ?? std.label}>
                  <td>
                    {(std.url ?? "").trim() ? (
                      <LinkToGuidance href={std.url.trim()} showInfoIcon>
                        {std.label || std.id}
                      </LinkToGuidance>
                    ) : (
                      <span>{std.label || std.id}</span>
                    )}
                  </td>
                  <td>
                    <ul className="list-disc pl-5 m-0">
                      {(std.chapters || [])
                        .filter((sourceId) => isStandardChapterIncludedInReport(evaluation, sourceId))
                        .map((sourceId) => (
                          <li key={sourceId}>
                            {getChapterDisplayLabel(std, sourceId)}
                          </li>
                        ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </ReportPreviewSection>

        <ReportPreviewSection id="report-terms" title="Terms">
          <p className="mb-2 text-gray-800">
            The terms used in the Conformance Level information are defined as follows:
          </p>
          <ul className="acr-report-preview__terms-list mb-0 text-gray-900">
            {termsList.map((term) => (
              <li key={term.id ?? term.label}>
                <strong>{term.label}</strong>
                :
                {" "}
                {term.description}
              </li>
            ))}
          </ul>
        </ReportPreviewSection>

        {displayStandards.map((standard, stdIndex) => {
          const standardSectionId =
            standard.id != null && String(standard.id).trim() !== ""
              ? `report-standard-${String(standard.id).replace(/[^a-zA-Z0-9_-]/g, "-")}`
              : `report-standard-${stdIndex}`;
          return (
            <ReportPreviewSection
              key={standardSectionId}
              id={standardSectionId}
              title={standard.report_heading}
              asSection
            >
                {(standard.chapters || []).map((sourceChapterId) => {
                  const catalogChapter = getCatalogChapter(catalog, sourceChapterId);
                  const chapterData = evaluation?.chapters?.[sourceChapterId] ?? {};
                  if (!catalogChapter) return null;
                  if (chapterData.disabled && !String(chapterData.notes ?? "").trim()) return null;

              const mappedHeading = getChapterDisplayLabel(standard, sourceChapterId);
                  const criteriaList = catalogChapter.criteria ?? [];

                  const chapterAnchorId = `report-chapter-${sourceChapterId}`;
                  return (
                    <ReportChapterTableDetails
                      key={sourceChapterId}
                      id={chapterAnchorId}
                      heading={mappedHeading}
                    >
                      {chapterData.notes ? (
                        <p className="acr-report-preview__multiline text-sm text-gray-800 mb-3">
                          <strong>Notes:</strong>
                          {" "}
                          {chapterData.notes}
                        </p>
                      ) : null}
                      {!chapterData.disabled && (
                        <div className="overflow-x-auto mb-0">
                          <table className="usa-table acr-report-preview__criteria-table">
                            <caption className="usa-sr-only">
                              {`Criteria, conformance levels, and remarks for ${mappedHeading}`}
                            </caption>
                            <thead>
                              <tr>
                                <th scope="col">Criteria</th>
                                <th scope="col">Conformance Level</th>
                                <th scope="col">Remarks and Explanations</th>
                              </tr>
                            </thead>
                            <tbody>
                              {criteriaList.map((catalogCrit) => (
                                <ReportCriteriaRow
                                  key={catalogCrit.id}
                                  catalog={catalog}
                                  catalogCrit={catalogCrit}
                                  chapterData={chapterData}
                                  getLevelLabel={getLevelLabel}
                                  sourceChapterId={sourceChapterId}
                                  standardId={standard.id ?? standard.label ?? standardSectionId}
                                  standardUrl={standard.url}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </ReportChapterTableDetails>
                  );
                })}
            </ReportPreviewSection>
          );
        })}

        {optionalBlock(evaluation?.legal_disclaimer, (
          <ReportPreviewSection
            id="report-legal-disclaimer"
            title={`Legal Disclaimer${vendor?.company_name ? ` (${vendor.company_name})` : ""}`}
          >
            <p className="acr-report-preview__multiline text-gray-900 mb-0">
              {evaluation.legal_disclaimer}
            </p>
          </ReportPreviewSection>
        ))}

        {optionalBlock(evaluation?.repository, (
          <ReportPreviewSection id="report-repository" title="Repository">
            <p className="mb-0">
              <TextLink href={evaluation.repository} target="_blank" rel="noopener noreferrer">
                {evaluation.repository}
                <ExternalLinkIcon />
                <span className="usa-sr-only"> (opens in new window)</span>
              </TextLink>
            </p>
          </ReportPreviewSection>
        ))}

        {optionalBlock(evaluation?.feedback, (
          <ReportPreviewSection id="report-feedback" title="Feedback">
            <p className="mb-0">
              <TextLink href={evaluation.feedback} target="_blank" rel="noopener noreferrer">
                {evaluation.feedback}
                <ExternalLinkIcon />
                <span className="usa-sr-only"> (opens in new window)</span>
              </TextLink>
            </p>
          </ReportPreviewSection>
        ))}

        {Array.isArray(evaluation?.related_openacrs) && evaluation.related_openacrs.length > 0 ? (
          <ReportPreviewSection id="report-related-openacrs" title="Related OpenACRs">
            <ul className="list-disc pl-6 space-y-2 mb-0">
              {(() => {
                const keyCounts = new Map();
                return evaluation.related_openacrs.map((related) => {
                  const baseKey = `${String(related?.url ?? "")}::${String(related?.type ?? "")}`;
                  const nextCount = (keyCounts.get(baseKey) || 0) + 1;
                  keyCounts.set(baseKey, nextCount);
                  return (
                    <li key={`${baseKey}::${nextCount}`}>
                  {related.url ? (
                    <TextLink href={related.url} target="_blank" rel="noopener noreferrer">
                      {related.url}
                      <ExternalLinkIcon />
                      <span className="usa-sr-only"> (opens in new window)</span>
                    </TextLink>
                  ) : (
                    "—"
                  )}
                  {related.type ? ` (${related.type})` : ""}
                    </li>
                  );
                });
              })()}
            </ul>
          </ReportPreviewSection>
        ) : null}

        {evaluation?.license ? (
          <p className="text-sm text-gray-700 mb-0">
            <strong>License:</strong>
            {" "}
            {evaluation.license}
          </p>
        ) : null}
      </article>

      <Pager label="Previous/Next">
        {lastChapter ? (
          <PagerLink to={`${ACR_EDITOR_CHAPTER_URL}/${lastChapter.id}`} direction="previous">
            {lastChapter.short_label ?? lastChapter.label ?? lastChapter.id}
          </PagerLink>
        ) : (
          <PagerLink to={ACR_EDITOR_ABOUT_URL} direction="previous">
            About
          </PagerLink>
        )}
        <PagerLink to={ACR_EDITOR_GLOSSARY_URL} direction="next">
          Glossary
        </PagerLink>
      </Pager>
    </div>
  );
}

function ReportCriteriaRow({
  catalog,
  catalogCrit,
  chapterData,
  getLevelLabel,
  sourceChapterId,
  standardId,
  standardUrl,
}) {
  const critId = catalogCrit.id;
  const rowId = reportCriterionRowId(standardId, sourceChapterId, critId);
  const evalCrit = (chapterData.criteria ?? []).find(
    (c) => String(c.num) === String(critId)
  );
  const handle = catalogCrit.handle ?? critId;
  const components = evalCrit?.components ?? catalogCrit.components?.map((co) => ({
    name: typeof co === "string" ? co : co?.name,
    adherence: {},
  })) ?? [];

  const guidanceHref = criteriaGuidanceHref(
    catalog,
    sourceChapterId,
    critId,
    standardUrl
  );
  const count = levelCountWithLevel(components);
  const countClass = `acr-report-preview__component-level-count-${count}`;

  const withLevel = components.filter((c) => c?.adherence?.level);
  const withNotes = components.filter((c) => String(c?.adherence?.notes ?? "").trim());

  return (
    <tr id={rowId} className="acr-report-preview__result-row">
      <td>
        {guidanceHref ? (
          <LinkToGuidance href={guidanceHref} showInfoIcon>
            {`${critId}: ${handle}`}
          </LinkToGuidance>
        ) : (
          <span className="text-gray-900">{`${critId}: ${handle}`}</span>
        )}
      </td>
      <td>
        <ul className={`acr-report-preview__conformance-list ${countClass}`}>
          {withLevel.map((comp) => (
            <li key={comp.name}>
              <span className="acr-report-preview__component-level-label">
                <strong>{componentLabelForReport(comp.name)}</strong>
                :
                {" "}
              </span>
              <span className="inline-block">{getLevelLabel(comp.adherence?.level)}</span>
            </li>
          ))}
        </ul>
      </td>
      <td>
        <ul className={`acr-report-preview__remarks-list ${countClass}`}>
          {withNotes.map((comp) => (
            <li key={`${comp.name}-notes`}>
              <span className="acr-report-preview__component-level-label">
                <strong>{componentLabelForReport(comp.name)}</strong>
                :
                {" "}
              </span>
              <span className="acr-report-preview__multiline inline-block align-top">
                {comp.adherence.notes}
              </span>
            </li>
          ))}
        </ul>
      </td>
    </tr>
  );
}

ReportCriteriaRow.propTypes = {
  catalog: PropTypes.object.isRequired,
  catalogCrit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handle: PropTypes.string,
    components: PropTypes.array,
  }).isRequired,
  chapterData: PropTypes.object.isRequired,
  getLevelLabel: PropTypes.func.isRequired,
  sourceChapterId: PropTypes.string.isRequired,
  standardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  standardUrl: PropTypes.string,
};

ReportCriteriaRow.defaultProps = {
  standardUrl: "",
};

function ReportStatusBanner({
  validationResult,
  submitSucceeded = false,
  productId = null,
}) {
  if (submitSucceeded) {
    return (
      <Alert
        type="success"
        heading="Report submitted"
        role="status"
        aria-live="polite"
      >
        Your report has been submitted successfully.
        {" "}
        {productId && (
          <Link className="text-blue-700 underline" to={`/product-details/${productId}`}>
            View product details
          </Link>
        )}
      </Alert>
    );
  }

  if (validationResult.isValid) {
    return (
      <Alert
        type="success"
        heading="Validation passed"
        role="status"
        aria-live="polite"
      >
        All About page fields with format requirements are valid.
      </Alert>
    );
  }

  return (
    <Alert type="error" heading="Validation failed" aria-live="assertive">
      <p>
        Fix the following fields on the About page before finalizing your report.
      </p>
      <ul className="list-disc pl-6 mt-2">
        {validationResult.failures.map((failure) => (
          <li key={failure.path}>
            <Link
              className="text-blue-700 underline"
              to={`${ACR_EDITOR_ABOUT_URL}#${failure.fieldId}`}
            >
              {failure.label}
            </Link>
            : {failure.message}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

ReportStatusBanner.propTypes = {
  validationResult: PropTypes.shape({
    isValid: PropTypes.bool.isRequired,
    failures: PropTypes.arrayOf(PropTypes.shape({
      path: PropTypes.string.isRequired,
      fieldId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  submitSucceeded: PropTypes.bool,
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function SubmitFailureBanner({
  errorType,
  serverMessage,
  missingReportMessage,
}) {
  return (
    <Alert type="error" heading="Submit failed" aria-live="assertive">
      {errorType === "missing-report-id" ? (
        <>
          {missingReportMessage}
          {" "}
          <Link className="text-blue-700 underline" to={ACR_EDITOR_ABOUT_URL}>
            Go to About page
          </Link>
        </>
      ) : serverMessage}
    </Alert>
  );
}

SubmitFailureBanner.propTypes = {
  errorType: PropTypes.oneOf(["server", "missing-report-id"]).isRequired,
  serverMessage: PropTypes.string.isRequired,
  missingReportMessage: PropTypes.string.isRequired,
};
